import { Params } from '@angular/router';

import { createFeatureSelector } from '@ngrx/store';

import { environment } from '../../environments/environment';
import {
  DealSectionFragments,
  DealSectionShortCode,
  DealSectionTitles,
} from '../../features/deal/constants/code-of-deal-keys';
import { Deal } from '../../features/deal/models/deal.model';
import { Transaction } from '../../features/home/models/transaction.model';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { arrayToPropertyValueRecord, arrayToRecord } from '../../features/shared/utils/array-to-record';
import { DateHelper } from '../../features/shared/utils/date-helper';
import { TopicListItem } from '../../features/topic/models/topic-list-item.model';
import { ChecklistListSelectors } from '../checklist-list/checklist-list.selectors';
import { RootState } from '../root-state.model';
import { MenuNode, MenuNodeTypes, MenuState, MenuType, MenuTypeDisplayNames } from './menu.state';
import { Permission, PermissionRelation } from '../../features/shared/models/api/permissions.enum';
import { createReferenceMemoizedSelector } from '../create-memoized-selector';
import { DatapointFieldListSelectors } from '../datapoint-field-list/datapoint-field-list.selectors';
import { DealSelectors } from '../deal/deal.selectors';
import { DealComparisonSelectors } from '../deal-comparison/deal-comparison.selectors';
import { PanelSelectors } from '../panel/panel.selectors';
import { ExtractSelectors } from '../extract/extract.selectors';
import { RouterSelectors } from '../router/router.selector';
import { TopicListSelectors } from '../topic-list/topic-list.selectors';
import { UserProfileSelectors } from '../user-profile/user-profile.selectors';
import { ColumnId, Column } from '../../features/market-comparison/models/column.model';
import { ColumnType } from '../../features/market-comparison/models/column-type.enum';
import { DatapointField } from '../../features/unified-datapoint/models/datapoint-field.models';
import { Topic } from '../../features/market-comparison/models/topic.model';

const DEAL_PAGE_REGEX = new RegExp(`/${SemerisRoutes.transactions.root}/[^/]*/[^/]*/[^/]*$`);
const DEAL_COMPARE_PAGE_REGEX = new RegExp(
  `^/${SemerisRoutes.transactions.root}/${SemerisRoutes.transactions.compare}.*`
);
const MARKET_COMPARE_PAGE_REGEX = new RegExp(`^/${SemerisRoutes.marketComparison.root}/.*`);

// Routes that exist only on `next`, so `SemerisRoutes` has no members for them.
const NEXT_PATHS = {
  allTools: '/all-tools',
  aiChat: '/ai-chat',
} as const;

interface ExternalParameters {
  selectedDeal?: Deal;
  comparisonDeals?: Deal[];
  extractOrder?: string[];
  topics?: TopicListItem[];
  queryParams?: Params;
  selectedChecklistTopics?: Topic[];
  datapointFieldsByColumnId?: Record<ColumnId, DatapointField>;
  selectedEditFieldHash?: string;
}

export namespace MenuSelectors {
  const PREFIX = 'MenuSelectors';
  const selectFeature = createFeatureSelector<MenuState>('menu');
  const url = (state: RootState) => state.menu.url;

  export const visibilityQueue = createReferenceMemoizedSelector(
    selectFeature,
    (state: MenuState) => state.visibilityQueue,
    `${PREFIX}.visibilityQueue`
  );
  export const selectedMenuRaw = createReferenceMemoizedSelector(
    selectFeature,
    (state: MenuState) => state.selectedMenu,
    `${PREFIX}.selectedMenu`
  );
  export const collapsedMenuItems = createReferenceMemoizedSelector(
    selectFeature,
    (state: MenuState) => state.collapsedMenuItems,
    `${PREFIX}.collapsedMenuItems`
  );

  export const allowedMenus = createReferenceMemoizedSelector(
    url,
    PanelSelectors.showJsonEditor,
    (selectedUrl: string, jsonEditorOpen: boolean) => {
      const result = [MenuType.Default];

      if (DEAL_PAGE_REGEX.test(selectedUrl)) {
        result.push(MenuType.Deal);

        if (jsonEditorOpen) {
          result.push(MenuType.DealEditor);
        }
      }

      if (DEAL_COMPARE_PAGE_REGEX.test(selectedUrl)) {
        result.push(MenuType.DealCompare);
      }

      if (MARKET_COMPARE_PAGE_REGEX.test(selectedUrl)) {
        result.push(MenuType.MarketCompare);
      }

      return result;
    },
    `${PREFIX}.allowedMenus`
  );

  export const preferedMenu = createReferenceMemoizedSelector(
    url,
    PanelSelectors.showJsonEditor,
    (selectedUrl: string, jsonEditorOpen: boolean): MenuType => {
      let result = MenuType.Default;

      if (DEAL_PAGE_REGEX.test(selectedUrl)) {
        result = jsonEditorOpen ? MenuType.DealEditor : MenuType.Deal;
      }

      if (DEAL_COMPARE_PAGE_REGEX.test(selectedUrl)) {
        result = MenuType.DealCompare;
      }

      if (MARKET_COMPARE_PAGE_REGEX.test(selectedUrl)) {
        result = MenuType.MarketCompare;
      }

      return result;
    },
    `${PREFIX}.preferedMenu`
  );

  export const selectedMenu = createReferenceMemoizedSelector(
    allowedMenus,
    preferedMenu,
    selectedMenuRaw,
    (allowed, prefered, selected): MenuType => {
      if (allowed.includes(selected)) {
        return selected;
      } else {
        return prefered;
      }
    },
    `${PREFIX}.selectedMenu`
  );

  export const externalMenuParameters = createReferenceMemoizedSelector(
    DealSelectors.deal,
    DealComparisonSelectors.deals,
    ChecklistListSelectors.selectedChecklistTopicIds,
    TopicListSelectors.topicListItemsUnfiltered,
    RouterSelectors.selectQueryParams,
    ChecklistListSelectors.selectedChecklistTopics,
    DatapointFieldListSelectors.datapointFieldsInRecord,
    ExtractSelectors.selectedDatapointForEdit,
    (
      selectedDeal?: Deal,
      comparisonDeals?: Deal[],
      extractOrder?: string[],
      topics?: TopicListItem[],
      queryParams?: Params,
      selectedChecklistTopics?: Topic[],
      datapointFieldsByColumnId?: Record<ColumnId, DatapointField>,
      selectedDatapointForEdit?: { datapointField?: DatapointField }
    ): ExternalParameters => ({
      selectedDeal,
      comparisonDeals,
      extractOrder,
      topics,
      queryParams,
      selectedChecklistTopics,
      datapointFieldsByColumnId,
      selectedEditFieldHash: selectedDatapointForEdit?.datapointField?.fieldHash,
    }),
    `${PREFIX}.externalMenuParameters`
  );

  export const menuItems = createReferenceMemoizedSelector(
    externalMenuParameters,
    UserProfileSelectors.dateFormat,
    selectedMenu,
    allowedMenus,
    visibilityQueue,
    (
      externalParameters: ExternalParameters,
      dateFormat: string,
      selectedMenu?: MenuType,
      allowedMenus?: MenuType[],
      visibilityQueue?: string[]
    ): MenuNode[] => {
      let menuItems: MenuNode[];

      switch (selectedMenu) {
        default:
        case MenuType.Default:
          menuItems = getMainMenu(allowedMenus);
          break;
        case MenuType.Deal:
          menuItems = getDealMenu(
            externalParameters.selectedDeal,
            dateFormat,
            externalParameters.extractOrder,
            externalParameters.topics,
            externalParameters.queryParams
          );
          break;
        case MenuType.DealEditor:
          menuItems = getDealEditorMenu(
            externalParameters.selectedDeal,
            externalParameters.selectedChecklistTopics,
            externalParameters.datapointFieldsByColumnId
          );
          break;
        case MenuType.DealCompare:
          menuItems = getDealCompareMenu(
            externalParameters.comparisonDeals,
            externalParameters.extractOrder,
            externalParameters.topics,
            externalParameters.queryParams
          );
          break;
        case MenuType.MarketCompare:
          menuItems = getMarketCompareMenu(externalParameters.topics);
          break;
      }

      return menuItems.map((i) => fillActive(i, visibilityQueue, externalParameters.selectedEditFieldHash));
    },
    `${PREFIX}.menuItems`
  );
}

function getMarketCompareMenu(topics: TopicListItem[]): MenuNode[] {
  return [
    {
      name: 'Back to Main Menu',
      icon: 'arrow_back',
      type: MenuNodeTypes.Navigation,
      navigateTo: MenuType.Default,
    },
    {
      name: 'Favorite Topics',
      type: MenuNodeTypes.Category,
    },
    ...getTopicMenuNodes(topics),
  ];
}

function getDealCompareMenu(
  deals: Deal[],
  extractOrder?: string[],
  topics?: TopicListItem[],
  queryParams?: Params
): MenuNode[] {
  return [
    {
      name: 'Back to Main Menu',
      icon: 'arrow_back',
      type: MenuNodeTypes.Navigation,
      navigateTo: MenuType.Default,
    },
    {
      name: 'Checklist Topics',
      type: MenuNodeTypes.Category,
    },
    ...getComparisonMenu(deals, extractOrder, topics, queryParams),
  ];
}

function getTopicMenuNodes(topics: TopicListItem[]): MenuNode[] {
  return topics
    ?.filter((t) => t.showInMenu)
    .map((t) => ({
      name: t.nameWithReference,
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.marketComparison.root, t.id],
      shortCode: t.nameWithReference.slice(0, 3),
    }));
}

function fillActive(node: MenuNode, visibilityQueue: string[], selectedEditFieldHash?: string): MenuNode {
  const result = { ...node };

  const url = new URL(window.location.href);

  let isActive = false;

  if (result.children) {
    result.children = result.children.map((child) => fillActive(child, visibilityQueue, selectedEditFieldHash));
  }

  if (result.activeRegex) {
    // nosemgrep: detect-non-literal-regexp -- hardcoded activeRegex from menu definitions
    isActive = new RegExp(result.activeRegex).test(`${url.pathname}${url.search}${url.hash}`);
  } else if (result.type === MenuNodeTypes.Link) {
    const queryMatch = result.extras?.queryParams
      ? new URLSearchParams(result.extras?.queryParams).toString() === url.searchParams.toString()
      : true;
    const fragmentMatch = result.extras?.fragment ? `#${result.extras.fragment}` === url.hash : true;
    const pathMatch = url.pathname.endsWith(result.link?.join('/').replace('//', '/')) && queryMatch;
    const scrollMatch = pathMatch && visibilityQueue.includes(result.extras?.fragment);
    const strictPatchMatch = pathMatch && !result.extras?.fragment;
    const fragmentedPathMatch = pathMatch && !!url.hash && fragmentMatch;

    isActive = strictPatchMatch || fragmentedPathMatch || scrollMatch;
  } else if (result.type === MenuNodeTypes.JsonEditorField) {
    isActive = !!selectedEditFieldHash && result.fieldHash === selectedEditFieldHash;
  }

  const childActive = result.children?.some((child) => child.active);

  result.active = isActive || result.active || childActive;

  return result;
}

function getMainMenu(allowedMenus: MenuType[]): MenuNode[] {
  return [
    ...allowedMenus
      .filter((m) => m !== MenuType.Default)
      .map(
        (type): MenuNode => ({
          name: `Back to ${MenuTypeDisplayNames[type]}`,
          icon: 'arrow_forward',
          type: MenuNodeTypes.Navigation,
          navigateTo: type,
        })
      ),
    {
      name: 'Dashboard',
      permissions: [],
      type: MenuNodeTypes.Link,
      icon: 'dashboard',
      link: ['/', 'home'],
    },
    {
      name: 'Find and Review',
      permissions: [],
      type: MenuNodeTypes.Category,
    },
    {
      name: 'Deals',
      permissions: [],
      icon: 'gavel',
      type: MenuNodeTypes.Link,
      idAttr: 'menu-item-deals',
      link: ['/', SemerisRoutes.dealFilter.root, SemerisRoutes.dealFilter.filter],
      extras: { queryParams: { dealFilterMode: 'all' } },
      activeRegex: `^/deals/filter.*$`,
    },
    {
      name: 'Deal Publishing',
      icon: 'summarize',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.transactions.root],
      permissions: [Permission.WRITE_TRANSACTION],
    },
    {
      name: 'Managers',
      permissions: [],
      icon: 'business',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.managers.root],
    },
    {
      name: 'Text Search',
      icon: 'search',
      permissions: [Permission.READ_TEXT_SEARCH],
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.search.documentSearch],
    },
    {
      name: 'Compare',
      type: MenuNodeTypes.Category,
      permissions: [Permission.READ_DEAL_COMPARE, Permission.READ_MARKET_COMPARE, Permission.READ_DEAL_COMPARE_V2],
      permissionsRelation: PermissionRelation.OR,
    },
    {
      name: 'Deal Compare',
      icon: 'view_column',
      type: MenuNodeTypes.Link,
      permissions: [Permission.READ_DEAL_COMPARE],
      link: ['/', SemerisRoutes.transactions.root, SemerisRoutes.transactions.compare],
      activeRegex: `^/${SemerisRoutes.transactions.root}/${SemerisRoutes.transactions.compare}.*$`,
    },
    {
      name: 'Deal Compare Pro',
      icon: 'view_column',
      type: MenuNodeTypes.Link,
      permissions: [Permission.READ_DEAL_COMPARE_V2],
      link: [
        '/',
        SemerisRoutes.transactions.root,
        SemerisRoutes.transactions.v2.root,
        SemerisRoutes.transactions.v2.compare.root,
      ],
      activeRegex: `^/${SemerisRoutes.transactions.root}/${SemerisRoutes.transactions.v2.root}/${SemerisRoutes.transactions.v2.compare.root}.*$`,
    },
    {
      name: 'Market Compare',
      icon: 'view_list',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.topic.root],
      permissions: [Permission.READ_MARKET_COMPARE],
      activeRegex: `^/(market-compare.*|topic.*)$`,
    },
    {
      name: 'Collaborate',
      type: MenuNodeTypes.Category,
      permissions: [
        Permission.READ_COMMENT,
        Permission.WRITE_LABEL_ADMIN,
        Permission.READ_LABEL,
        Permission.WRITE_LABEL,
      ],
      permissionsRelation: PermissionRelation.OR,
    },
    {
      name: 'Notes',
      icon: 'comment',
      type: MenuNodeTypes.Link,
      idAttr: 'menu-item-notes',
      link: ['/', SemerisRoutes.notes],
      activeRegex: `^/${SemerisRoutes.notes}.*$`,
      permissions: [Permission.READ_COMMENT],
      permissionsRelation: PermissionRelation.OR,
    },
    {
      name: 'Labels',
      icon: 'label',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.labels],
      activeRegex: `^/${SemerisRoutes.labels}.*$`,
      permissions: [Permission.WRITE_LABEL_ADMIN, Permission.READ_LABEL, Permission.WRITE_LABEL],
      permissionsRelation: PermissionRelation.OR,
    },
    {
      name: 'Build',
      permissions: [],
      type: MenuNodeTypes.Category,
    },
    {
      name: 'Datapoints',
      permissions: [],
      icon: 'data_array',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.datapoint.root],
      activeRegex: `^/${SemerisRoutes.datapoint.root}(\\?.*)?$`,
    },
    {
      name: 'Topics',
      permissions: [Permission.READ_MARKET_COMPARE],
      icon: 'view_list',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.topic.root],
      activeRegex: `^/(market-compare.*|topic.*)$`,
    },
    {
      name: 'Checklists',
      icon: 'checklist_rtl',
      permissions: [],
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.checklist.root],
      activeRegex: `^/(checklist).*$`,
    },
    {
      name: 'Formula Builder',
      icon: 'science',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.formula.root],
      activeRegex: `^/${SemerisRoutes.formula.root}.*$`,
      permissions: [Permission.WRITE_FORMULA],
      icons: [
        {
          icon: 'source',
          tooltip: 'Formula Editor',
          link: ['/', SemerisRoutes.formula.root, 'empty'],
        },
      ],
    },
    {
      name: 'Upload',
      type: MenuNodeTypes.Category,
      permissions: [Permission.READ_DOCUMENT, Permission.WRITE_DOCUMENT],
      permissionsRelation: PermissionRelation.OR,
    },
    {
      name: 'Uploaded Documents',
      icon: 'description',
      activeRegex: `^/${SemerisRoutes.documents.root}[^/]*$`,
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.documents.root],
      permissions: [Permission.READ_DOCUMENT],
    },
    {
      name: 'Submit Documents',
      icon: 'add',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.documents.root, SemerisRoutes.documents.create],
      permissions: [Permission.WRITE_DOCUMENT],
    },

    {
      name: 'Stip Tracker',
      type: MenuNodeTypes.Category,
      permissions: [Permission.WRITE_POSITION],
      permissionsRelation: PermissionRelation.OR,
    },

    {
      name: 'Stip Lists',
      icon: 'work',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.positionList.root],
      activeRegex: `^/${SemerisRoutes.positionList.root}$`,
      shortCode: 'POS',
      permissions: [Permission.WRITE_POSITION],
    },

    {
      name: 'Import Stip List',
      icon: 'upload',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.positionList.root, SemerisRoutes.positionList.import],
      activeRegex: `^/${SemerisRoutes.positionList.root}/${SemerisRoutes.positionList.import}$`,
      shortCode: 'IPL',
      permissions: [Permission.WRITE_POSITION],
    },

    {
      name: 'Counterparties',
      icon: 'groups_3',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.positionList.root, SemerisRoutes.positionList.counterparties],
      activeRegex: `^/${SemerisRoutes.positionList.root}/${SemerisRoutes.positionList.counterparties}$`,
      shortCode: 'CNT',
      permissions: [Permission.WRITE_POSITION, Permission.READ_POSITION_COUNTERPARTY],
      permissionsRelation: PermissionRelation.AND,
    },

    {
      name: 'Tickler File',
      type: MenuNodeTypes.Category,
      permissions: [Permission.READ_TICKLER, Permission.READ_CALENDAR],
      permissionsRelation: PermissionRelation.OR,
    },
    {
      name: 'Deal Events',
      icon: 'schedule',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.tickler],
      activeRegex: `^/${SemerisRoutes.tickler}.*$`,
      permissions: [Permission.READ_TICKLER],
    },
    {
      name: 'Calendars',
      icon: 'calendar_month',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.calendar],
      activeRegex: `^/${SemerisRoutes.admin.root}/${SemerisRoutes.admin.calendar}.*$`,
      permissions: [Permission.READ_CALENDAR],
    },

    {
      name: 'User Admin',
      type: MenuNodeTypes.Category,
      permissions: [
        Permission.READ_USER,
        Permission.READ_TENANT,
        Permission.READ_PERMISSION,
        Permission.READ_ROLE,
        Permission.READ_USER_ADMIN,
        Permission.WRITE_USER_ADMIN,
        Permission.WRITE_ACTIVITY_REPORT,
      ],
      permissionsRelation: PermissionRelation.OR,
    },

    {
      name: 'Users',
      shortCode: 'USR',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.users],
      permissions: [Permission.READ_USER, Permission.READ_USER_ADMIN, Permission.WRITE_USER_ADMIN],
      permissionsRelation: PermissionRelation.OR,
    },
    {
      name: 'Users without tenants',
      shortCode: 'UST',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.usersWithoutTenants],
      extras: {
        queryParams: {
          active: 'displayName',
          direction: 'asc',
          sortBy: 'lastLogin',
          sortDirection: 'desc',
          tenantShortName: ['Not defined', environment.onboardingTenantShortName],
        },
      },
      activeRegex: `^/${SemerisRoutes.admin.root}/${SemerisRoutes.admin.usersWithoutTenants}.*$`,
      permissions: [Permission.WRITE_USER_ADMIN, Permission.READ_USER_ADMIN],
      permissionsRelation: PermissionRelation.OR,
    },
    {
      name: 'User Activations',
      shortCode: 'USA',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.userActivation],
      permissions: [Permission.READ_USER_ACTIVATION],
    },
    {
      name: 'Tenants',
      shortCode: 'TEN',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.tenants.root],
      permissions: [Permission.READ_TENANT],
    },
    {
      name: 'Permissions',
      shortCode: 'PER',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.permissions],
      permissions: [Permission.READ_PERMISSION],
    },
    {
      name: 'Roles',
      shortCode: 'ROL',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.roles],
      permissions: [Permission.READ_ROLE],
    },

    {
      name: 'Datapoint Sets',
      shortCode: 'DPS',
      type: MenuNodeTypes.Link,
      permissions: [Permission.WRITE_DATAPOINT_SET],
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.datapointSet],
    },

    {
      name: 'Activity Reporting',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.activityReporting.root],
      activeRegex: `^/${SemerisRoutes.admin.root}/${SemerisRoutes.admin.activityReporting.root}.*$`,
      shortCode: 'ACT',
      permissions: [Permission.WRITE_ACTIVITY_REPORT],
    },

    {
      name: 'Tools',
      type: MenuNodeTypes.Category,
      permissions: [
        Permission.WRITE_HELP_AI_ADMIN,
        Permission.READ_NEWS_CRAWL,
        Permission.WRITE_SYNC_ADMIN,
        Permission.READ_TENANT_EXPORT,
        Permission.WRITE_API,
        Permission.WRITE_CATALOG,
        Permission.WRITE_DATAPOINT_SET,
        Permission.READ_BILLING,
        Permission.READ_BILLING_ADMIN,
        Permission.READ_DATAPOINT_ORGANIZER,
        Permission.READ_MANAGE_DEAL_QUEUE,
        Permission.READ_TERM_SYNONYM_ORGANIZER,
        Permission.WRITE_FILE_SHARE,
      ],
      permissionsRelation: PermissionRelation.OR,
    },

    {
      name: 'Deal Queue',
      icon: 'web_stories',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.transactions.root, SemerisRoutes.transactions.queue],
      activeRegex: `^/${SemerisRoutes.transactions.root}/${SemerisRoutes.transactions.queue}.*$`,
      permissions: [Permission.READ_MANAGE_DEAL_QUEUE],
    },

    {
      name: 'Catalog',
      icon: 'view_quilt',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.catalog],
      activeRegex: `^/${SemerisRoutes.admin.root}/${SemerisRoutes.admin.catalog}.*$`,
      permissions: [Permission.WRITE_CATALOG],
      versions: ['local', 'dev', 'staging'],
    },

    {
      name: 'Publish Queue',
      icon: 'library_books',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.publication],
      activeRegex: `^/${SemerisRoutes.publication}.*$`,
      permissions: [Permission.READ_PUBLICATION],
    },

    {
      name: 'News Scanner',
      icon: 'feed',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.newsFeed],
      activeRegex: `^/${SemerisRoutes.newsFeed}.*$`,
      permissions: [Permission.READ_NEWS_CRAWL],
    },

    {
      name: 'Datapoint Organizer',
      icon: 'insert_chart',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.datapointOrganizer.root],
      activeRegex: `^/${SemerisRoutes.datapointOrganizer.root}.*$`,
      permissions: [Permission.READ_DATAPOINT_ORGANIZER],
    },

    {
      name: 'Term Synonym Organizer',
      icon: 'swap_horiz',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.termSynonymOrganizer.root],
      activeRegex: `^/${SemerisRoutes.termSynonymOrganizer.root}.*$`,
      permissions: [Permission.READ_TERM_SYNONYM_ORGANIZER],
    },

    {
      name: 'Billing',
      icon: 'attach_money',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.billing],
      activeRegex: `^/${SemerisRoutes.billing}.*$`,
      permissions: [Permission.READ_BILLING, Permission.READ_BILLING_ADMIN],
      permissionsRelation: PermissionRelation.OR,
    },

    {
      name: 'Help AI Conversations',
      icon: 'live_help',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.conversations.root, SemerisRoutes.conversations.helpAi],
      activeRegex: `^/${SemerisRoutes.conversations.root}/${SemerisRoutes.conversations.helpAi}.*$`,
      permissions: [Permission.WRITE_HELP_AI_ADMIN],
    },

    {
      name: 'Sync Metadata',
      type: MenuNodeTypes.Link,
      icon: 'sync',
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.syncMetadata],
      permissions: [Permission.WRITE_SYNC_ADMIN],
      shortCode: 'SYC',
      versions: ['local', 'dev', 'staging'],
    },
    {
      name: 'Extraction Status',
      type: MenuNodeTypes.Link,
      icon: 'timeline',
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.extractionStatus],
      permissions: [Permission.WRITE_DATAPOINT_RUNNER_ADMIN, Permission.READ_DEPENDENCY_TREE],
      permissionsRelation: PermissionRelation.AND,
      shortCode: 'EXS',
    },
    {
      name: 'Data Export',
      shortCode: 'TDE',
      icon: 'cloud_download',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.tenants.root, SemerisRoutes.admin.tenants.export],
      permissions: [Permission.READ_TENANT_EXPORT],
    },
    {
      name: 'Integrations',
      icon: 'integration_instructions',
      withoutAsset: true,
      permissions: [Permission.WRITE_API],
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.integrations.root],
    },
    {
      name: 'Embeddings',
      icon: 'hub',
      shortCode: 'EMB',
      withoutAsset: true,
      permissions: [Permission.WRITE_EMBEDDINGS],
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.embeddings.root],
    },
    {
      name: 'Zing server status',
      icon: 'monitor_heart',
      shortCode: 'ZNG',
      withoutAsset: true,
      permissions: [Permission.READ_ZING_DIAGNOSTICS],
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.zingDiagnostics.root],
    },
    {
      name: 'File Sharing',
      icon: 'upload_file',
      shortCode: 'FSH',
      withoutAsset: true,
      permissions: [Permission.WRITE_FILE_SHARE],
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.admin.root, SemerisRoutes.admin.fileSharing.root],
    },

    {
      name: 'Labs',
      type: MenuNodeTypes.Category,
      permissions: [Permission.READ_ANALYSIS_ACCELERATOR],
      permissionsRelation: PermissionRelation.OR,
    },
    {
      name: 'Analysis Accelerator',
      icon: 'rocket_launch',
      activeRegex: `^/${SemerisRoutes.analysisAccelerator.root}`,
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.analysisAccelerator.root],
      shortCode: 'CA',
      permissions: [Permission.READ_ANALYSIS_ACCELERATOR],
    },
    {
      name: 'Research',
      type: MenuNodeTypes.Category,
      permissions: [Permission.READ_ANALYST_CORNER],
    },
    {
      name: 'Analyst Corner',
      icon: 'article',
      activeRegex: `^/${SemerisRoutes.analystCorner.root}/.*$`,
      type: MenuNodeTypes.Link,
      shortCode: 'AC',
      link: ['/', SemerisRoutes.analystCorner.root],
      permissions: [Permission.READ_ANALYST_CORNER],
    },

    {
      name: 'In Next',
      type: MenuNodeTypes.Category,
      nextOnly: true,
      permissions: [Permission.READ_NEXT_UI],
    },
    {
      name: 'All Tools',
      icon: 'apps',
      type: MenuNodeTypes.Button,
      shortCode: 'NXT',
      nextOnly: true,
      nextOnlyPath: NEXT_PATHS.allTools,
      permissions: [Permission.READ_NEXT_UI],
    },
    {
      name: 'AI Agent',
      icon: 'auto_awesome',
      type: MenuNodeTypes.Button,
      shortCode: 'NXTAI',
      nextOnly: true,
      nextOnlyPath: NEXT_PATHS.aiChat,
      permissions: [Permission.READ_NEXT_UI, Permission.WRITE_AI_CHAT],
    },

    {
      name: 'Help',
      type: MenuNodeTypes.Category,
      accessToBlockedUsers: true,
    },
    {
      name: 'User Guide',
      icon: 'help_outline',
      activeRegex: '^/help',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.help.root, 'main.html'],
      shortCode: 'HLP',
      permissions: [],
    },
    {
      name: 'Upgrade Your Edition',
      icon: 'flash_on',
      activeRegex: '^/upgrade',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.upgrade.root],
      shortCode: 'UP',
      permissions: [Permission.UPGRADE],
    },
    {
      name: 'Contact us',
      icon: 'feedback',
      activeRegex: '^/feedback',
      type: MenuNodeTypes.Button,
      shortCode: 'FBK',
      accessToBlockedUsers: true,
    },
  ];
}

function getComparisonMenu(
  deals: Deal[],
  extractOrder?: string[],
  topics?: TopicListItem[],
  queryParams?: Params
): MenuNode[] {
  if (!deals) {
    return [];
  }

  const commands = ['/', SemerisRoutes.transactions.root, SemerisRoutes.transactions.compare];

  return topicIdsToMenuNodes(
    extractOrder || BUILT_IN_TOPICS,
    topics,
    commands,
    queryParams
  );
}

function getDealEditorMenu(
  deal: Deal | Transaction,
  selectedChecklistTopics?: Topic[],
  datapointFieldsByColumnId?: Record<ColumnId, DatapointField>
): MenuNode[] {
  if (!deal) {
    return [];
  }

  const result: MenuNode[] = [
    {
      name: `Back to ${MenuTypeDisplayNames[MenuType.Deal]}`,
      icon: 'arrow_back',
      type: MenuNodeTypes.Navigation,
      navigateTo: MenuType.Deal,
    },
    {
      name: 'JSON Editor Fields',
      type: MenuNodeTypes.Category,
    },
  ];

  if (!selectedChecklistTopics?.length || !datapointFieldsByColumnId) {
    return result;
  }

  for (const topic of selectedChecklistTopics) {
    const fieldNodes: MenuNode[] = (topic?.columns || [])
      .filter((c: Column) => c.type === ColumnType.Field)
      .map((column: Column): MenuNode | undefined => {
        const field = datapointFieldsByColumnId[column.id];
        if (!field?.editorMenuLabel) {
          return undefined;
        }
        return {
          name: field.editorMenuLabel,
          icon: 'data_object',
          type: MenuNodeTypes.JsonEditorField,
          columnId: column.id,
          fieldHash: field.fieldHash,
        };
      })
      .filter((n): n is MenuNode => !!n);

    if (fieldNodes.length) {
      result.push({
        name: topic.name || topic.id,
        type: MenuNodeTypes.Category,
      });
      result.push(...fieldNodes);
    }
  }

  return result;
}

function getDealMenu(
  deal: Deal | Transaction,
  dateFormat: string,
  extractOrder?: string[],
  topics?: TopicListItem[],
  queryParams?: Params
): MenuNode[] {
  if (!deal) {
    return [];
  }

  const commands = ['/', SemerisRoutes.transactions.root, deal.libraryId, deal.managerId, deal.transactionShortName];

  const documentMenuItems: MenuNode[] = deal.documents.map((doc) => ({
    name: `${doc.documentDisplayName || doc.documentId} ${doc.documentDate ? `(${DateHelper.format(doc.documentDate, dateFormat)})` : ''}`,
    type: MenuNodeTypes.Button,
    activeRegex: `documentShortName=${doc.documentId}`,
    icon: 'description',
    documentShortName: doc.documentId,
  }));

  return [
    {
      name: 'Back to Main Menu',
      icon: 'arrow_back',
      type: MenuNodeTypes.Navigation,
      navigateTo: MenuType.Default,
    },
    {
      name: deal.managerDisplayName,
      icon: 'business',
      type: MenuNodeTypes.Link,
      link: ['/', SemerisRoutes.dealFilter.root, SemerisRoutes.dealFilter.filter],
      extras: { queryParams: { managers: deal.managerId, defaultFilters: true } },
    },
    {
      name: deal.transactionDisplayShortName,
      type: MenuNodeTypes.Link,
      icon: 'summarize',
      link: commands,
    },
    {
      name: 'Checklist Topics',
      type: MenuNodeTypes.Category,
    },
    ...topicIdsToMenuNodes(extractOrder || BUILT_IN_TOPICS, topics, commands, queryParams),
    {
      name: 'Documents',
      type: MenuNodeTypes.Category,
    },
    ...documentMenuItems,
  ];
}

const BUILT_IN_TOPICS = [DealSectionShortCode.Identifiers, DealSectionShortCode.Documents];

function getBuiltInNodes(commands: string[], queryParams: any): MenuNode[] {
  const result: MenuNode[] = BUILT_IN_TOPICS.map((shortCode) => ({
    shortCode,
    name: DealSectionTitles[shortCode],
    type: MenuNodeTypes.Link,
    link: commands,
    extras: { fragment: DealSectionFragments[shortCode], queryParams },
  }));

  return result;
}

function getBuiltInNodesRecord(commands: string[], queryParams: any): Record<string, MenuNode> {
  return arrayToRecord(getBuiltInNodes(commands, queryParams), 'shortCode');
}

function topicIdsToMenuNodes(
  topicIds: string[],
  topics: TopicListItem[],
  commands: string[],
  queryParams: any
): MenuNode[] {
  const topicRecord: Record<string, MenuNode> = {
    ...arrayToPropertyValueRecord(
      topics,
      'id',
      (topic): MenuNode => ({
        name: topic.nameWithReference,
        shortCode: topic.nameWithReference.slice(0, 3),
        type: MenuNodeTypes.Link,
        link: commands,
        extras: { queryParams, fragment: topic.id },
      })
    ),
    ...getBuiltInNodesRecord(commands, queryParams),
  };

  return topicIds.map((topicId) => topicRecord[topicId]).filter((v) => !!v);
}
